import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useMemo } from "react";
import DeleteBtn from "src/components/shared/DeleteBtn";
import UpdateBtn from "src/components/shared/UpdateBtn";
import { UNIT_LIST_CONVERT } from "src/constant";
import { convertIntMoney } from "src/helper/convertCurrency";
import { formatNumberWithDot } from "src/helper/convertNum";
import useWTProdRemove from "src/hooks/warehouse-transfer/useWTProdRemove";
const commonCss = { fontWeight: "bold", textAlign: "center" };

export default function ImportProductList({ list, setProdList, setProdPick, onUpdate, mutateList, readOnly }) {
  const { total, totalQty } = useMemo(() => {
    const result = list?.reduce((acc, curr) => {
      const price = Number(curr?.tempPrice) || 0;
      const qty = Number(curr?.qtyCal) || 0;
      const discount = Number(curr?.discount) || 0;
      const total = price * qty * (1 - discount / 100);
      return acc + total;
    }, 0);

    const group_g_kg = ["g", "kg"];
    const group_ml_l = ["ml", "l"];
    const group_piece_bottle_combo = ["piece", "bottle", "combo"];

    let liquidUnitsCount = 0;
    let itemUnitsCount = 0;
    let weightUnitsCount = 0;

    list?.forEach((item) => {
      if (group_ml_l.includes(item?.unit)) {
        liquidUnitsCount++;
      } else if (group_piece_bottle_combo.includes(item?.unit)) {
        itemUnitsCount++;
      } else if (group_g_kg.includes(item?.unit)) {
        weightUnitsCount++;
      }
    });

    let calQty = 0;
    const isMixedUnits = (liquidUnitsCount > 0 && (itemUnitsCount > 0 || weightUnitsCount > 0)) || (itemUnitsCount > 0 && weightUnitsCount > 0);

    if (!isMixedUnits) {
      calQty = list?.reduce((acc, curr) => {
        return acc + Number(curr?.qtyCal);
      }, 0);
    }

    const total = (result / 1000) * 1000;
    const totalQty = isMixedUnits ? 0 : calQty;
    return { total, totalQty };
  }, [list]);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        position: "sticky",
        top: 0,
        border: "1px solid var(--grey)",
        borderRadius: "12px",
      }}
    >
      <Table
        sx={{
          minWidth: 800,
        }}
      >
        <TableHead>
          <TableRow sx={{ background: "var(--light-grey)" }}>
            <TableCell sx={{ ...commonCss }}>STT</TableCell>
            <TableCell sx={{ ...commonCss }}>Tên SP</TableCell>
            <TableCell sx={{ ...commonCss }}>Số lô nhập</TableCell>
            <TableCell sx={{ ...commonCss }}>SL</TableCell>
            <TableCell sx={{ ...commonCss }}>CK (%)</TableCell>
            <TableCell sx={{ ...commonCss }}>Đơn giá (VNĐ)</TableCell>
            <TableCell sx={{ ...commonCss }}>Thành tiền </TableCell>
            <TableCell sx={{ ...commonCss }}>Thao tác</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!!list?.length ? (
            list?.map((v, index) => {
              return (
                <Item
                  key={index}
                  num={index + 1}
                  item={v}
                  setProdList={setProdList}
                  onUpdate={onUpdate}
                  setProdPick={setProdPick}
                  mutateList={mutateList}
                  readOnly={readOnly}
                />
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={8}
                align="center"
              >
                 Không có dữ liệu 
              </TableCell>
            </TableRow>
          )}
          <TableRow sx={{ background: "var(--light-grey)", height: "30px" }}>
            <TableCell align="center"> </TableCell>
            <TableCell align="center">
              <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>Tổng</Typography>
            </TableCell>
            <TableCell align="center"> </TableCell>
            <TableCell align="center">
              <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>{totalQty === 0 ? "" : totalQty}</Typography>
            </TableCell>
            <TableCell align="center">  </TableCell>
            <TableCell align="center"> </TableCell>
            <TableCell align="center">
              <Typography sx={{ fontWeight: "bold", fontSize: "14px" }}>{convertIntMoney(total) ?? 0}đ</Typography>
            </TableCell>
            <TableCell align="center"> </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const Item = ({ item, num, setProdPick, onUpdate, list, setProdList, mutateList, readOnly }) => {
  const { trigger: removeProdSlip } = useWTProdRemove(item?.warehouseTransfer);

  const handleRemove = async (productModelId) => {
    const res = await removeProdSlip({
      pathIncluded: {
        transferDetailId: item?._id,
      },
    });
    if (res?.success) {
      if (mutateList) {
        await mutateList();
      }
      setProdList(list?.filter((v) => v?.productModelId !== productModelId));
      setProdPick();
      return enqueueSnackbar("Xoá sản phẩm thành công!", { variant: "success" });
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };
  return (
    <TableRow>
      <TableCell sx={{ ...commonCss }}>{num}</TableCell>
      <TableCell sx={{ ...commonCss }}>
        <Box>
          <Typography sx={{ fontSize: "12px" }}>{item?.productName}</Typography>
          <Typography sx={{ fontSize: "10px" }}>({item?.productMolName})</Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ ...commonCss }}>{item?.batchName?.batch?.code ?? item?.batchName}</TableCell>
      <TableCell sx={{ ...commonCss }}>
        {formatNumberWithDot(Number(item?.qtyCal))} {item?.unit && `(${UNIT_LIST_CONVERT[item?.unit]})`}
      </TableCell>
      <TableCell sx={{ ...commonCss }}>{item?.discount}</TableCell>
      <TableCell sx={{ ...commonCss }}>
        <Typography sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>{!!item?.tempPrice ? convertIntMoney(item?.tempPrice) + " đ" : 0}</Typography>
      </TableCell>
      <TableCell sx={{ ...commonCss }}>
        <Typography sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>{convertIntMoney(item?.tempPrice * Number(item?.qtyCal) * (1 - item?.discount / 100) ?? 0)} đ</Typography>
      </TableCell>
      <TableCell sx={{ ...commonCss }}>
        <Box
          display="flex"
          gap="4px"
          justifyContent="center"
        >
          <UpdateBtn
            disabled={readOnly || item?.qtyCal === item?.importedQty}
            handleUpdate={() => onUpdate({ ...item, mode: "update" })}
          />
          <DeleteBtn
            disabled={readOnly}
            handleDelete={() => handleRemove(item?.productModelId, item?.transferDetailId)}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
};
